'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';

export type PreviewMode = 'desktop' | 'tablet' | 'mobile';

interface PreviewModeContextValue {
  mode: PreviewMode;
  setMode: (mode: PreviewMode) => void;
}

const STORAGE_KEY = 'crm_preview_mode';
const WRAPPER_ID = 'preview-mode-wrapper';

const PreviewModeContext = createContext<PreviewModeContextValue>({
  mode: 'desktop',
  setMode: () => undefined,
});

/** Maps preview mode to max-width of the inner content wrapper. */
const MODE_MAX_WIDTH: Record<PreviewMode, string | null> = {
  desktop: null,
  tablet: '768px',
  mobile: '414px',
};

export function PreviewModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<PreviewMode>('desktop');

  // Read persisted preference on mount (client-only)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as PreviewMode | null;
      if (saved && saved in MODE_MAX_WIDTH) {
        setModeState(saved);
      }
    } catch {
      // localStorage unavailable (SSR safety)
    }
  }, []);

  // Apply CSS class to the wrapper element whenever mode changes
  useEffect(() => {
    const wrapper = document.getElementById(WRAPPER_ID);
    if (!wrapper) return;

    const maxWidth = MODE_MAX_WIDTH[mode];
    if (maxWidth) {
      wrapper.style.maxWidth = maxWidth;
      wrapper.style.margin = '0 auto';
      wrapper.style.boxShadow = '0 0 0 1px hsl(var(--border)), 0 4px 24px rgba(0,0,0,0.12)';
      wrapper.style.minHeight = '100vh';
      wrapper.style.overflow = 'hidden';
      wrapper.style.borderRadius = '4px';
    } else {
      wrapper.style.maxWidth = '';
      wrapper.style.margin = '';
      wrapper.style.boxShadow = '';
      wrapper.style.minHeight = '';
      wrapper.style.overflow = '';
      wrapper.style.borderRadius = '';
    }
  }, [mode]);

  const setMode = useCallback((next: PreviewMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  return (
    <PreviewModeContext.Provider value={{ mode, setMode }}>
      <div id={WRAPPER_ID} style={{ position: 'relative' }}>
        {children}
      </div>
    </PreviewModeContext.Provider>
  );
}

export function usePreviewMode(): PreviewModeContextValue {
  return useContext(PreviewModeContext);
}
